import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  updateCustomerStatus,
  deleteCustomer,
} from '../services/customerService';
import {
  fetchCustomerAddresses,
  createCustomerAddress,
  updateCustomerAddress,
  setDefaultCustomerAddress,
} from '../services/customerAddress';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash,
  Eye,
  Plus,
  Star,
} from 'lucide-react';

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', status: '1' });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [addressOpen, setAddressOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState({ label: 'Home', address: '', status: 1 });
  const [addressErrors, setAddressErrors] = useState({});
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressCustomerId, setAddressCustomerId] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState('');

  const loadCustomers = async () => {
    try {
      const res = await fetchCustomers({
        search,
        page,
        limit,
        sortBy,
        sortOrder,
        status: statusFilter,
      });
      const records = res.data?.data?.records || res.data?.data || [];
      const meta = res.data?.data?.meta || {};
      setCustomers(records);
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || records.length);
    } catch (err) {
      toast.error('Failed to load customers');
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [page, limit, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    if (search === '') {
      setPage(1);
      loadCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const exportToXls = () => {
    const headers = ['Name', 'Phone', 'Email'];
    const rows = customers.map((c) => [c.name, c.phone || '', c.email || '']);
    const table = [headers, ...rows]
      .map((r) => r.map((v) => `"${v ?? ''}"`).join('\t'))
      .join('\n');
    const blob = new Blob(['\ufeff', table], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'customers.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name) newErrors.name = 'Name is required';
    if (!form.phone) newErrors.phone = 'Phone is required';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const payload = { name: form.name, phone: form.phone, email: form.email, status: Number(form.status) };
      if (editingId) {
        await updateCustomer(editingId, payload);
        toast.success('Customer updated');
      } else {
        await createCustomer(payload);
        toast.success('Customer created');
      }
      closeForm();
      loadCustomers();
    } catch (err) {
      toast.error('Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm({ name: '', phone: '', email: '', status: '1' });
    setErrors({});
    setOpen(true);
  };

  const handleEdit = (cust) => {
    setEditingId(cust.id);
    setForm({
      name: cust.name || '',
      phone: cust.phone || '',
      email: cust.email || '',
      status: String(cust.status ?? 1),
    });
    setErrors({});
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({ name: '', phone: '', email: '', status: '1' });
    setErrors({});
    setOpen(false);
  };

  const toggleStatus = async (cust) => {
    const newStatus = cust.status === 1 || cust.status === '1' ? 2 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this customer?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateCustomerStatus(cust.id, newStatus);
      toast.success('Status updated');
      loadCustomers();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this customer?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteCustomer(id);
      toast.success('Customer deleted');
      loadCustomers();
    } catch (err) {
      toast.error('Failed to delete customer');
    }
  };

  const openAddressModal = async (cust) => {
    setAddressCustomerId(cust.id);
    setAddressForm({ label: 'Home', address: '', status: 1 });
    setAddressErrors({});
    setEditingAddressId(null);
    setAddressOpen(true);
    try {
      const res = await fetchCustomerAddresses(cust.id);
      setAddresses(res.data?.data || res.data || []);
    } catch (err) {
      toast.error('Failed to load addresses');
    }
  };

  const closeAddressModal = () => {
    setAddressOpen(false);
    setAddresses([]);
    setAddressForm({ label: 'Home', address: '', status: 1 });
    setAddressErrors({});
    setEditingAddressId(null);
  };

  const handleAddressEdit = (addr) => {
    setEditingAddressId(addr.id);
    setAddressForm({
      label: addr.label || 'Home',
      address: addr.address || '',
      status: addr.status ?? 1,
    });
  };

  const handleSetDefault = async (addr) => {
    try {
      await setDefaultCustomerAddress(addressCustomerId, addr.id);
      const res = await fetchCustomerAddresses(addressCustomerId);
      setAddresses(res.data?.data || res.data || []);
      toast.success('Default address updated');
    } catch (err) {
      toast.error('Failed to set default address');
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (!addressForm.address) {
      setAddressErrors({ address: 'Address is required' });
      return;
    }
    setAddressLoading(true);
    try {
      if (editingAddressId) {
        await updateCustomerAddress(addressCustomerId, editingAddressId, {
          label: addressForm.label,
          address: addressForm.address,
          status: addressForm.status,
        });
      } else {
        await createCustomerAddress(addressCustomerId, {
          label: addressForm.label,
          address: addressForm.address,
          status: addressForm.status,
          isDefault: addresses.length === 0,
        });
      }
      const res = await fetchCustomerAddresses(addressCustomerId);
      setAddresses(res.data?.data || res.data || []);
      setAddressForm({ label: 'Home', address: '', status: 1 });
      setAddressErrors({});
      setEditingAddressId(null);
    } catch (err) {
      toast.error('Failed to save address');
    } finally {
      setAddressLoading(false);
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadCustomers();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadCustomers();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <div className="flex space-x-2">
          <button
            onClick={exportToXls}
            className="bg-green-600 text-white px-4 py-2 rounded"
          >
            Export XLS
          </button>
          <button
            onClick={handleAdd}
            className="bg-indigo-600 text-white px-4 py-2 rounded"
          >
            Add Customer
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Phone</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Addresses</th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((cust) => (
              <tr key={cust.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{cust.name}</td>
                <td className="py-2 px-3">{cust.phone}</td>
                <td className="py-2 px-3">{cust.email}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-col items-start">
                    <span>{cust.addressCount || 0}</span>
                    <div className="flex space-x-1 mt-1">
                      <button className="text-gray-600" onClick={() => openAddressModal(cust)}>
                        <Eye size={16} />
                      </button>
                      <button className="text-green-600" onClick={() => openAddressModal(cust)}>
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3">{new Date(cust.createdAt).toLocaleDateString()}</td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${cust.status === 1 || cust.status === '1' ? 'bg-green-500' : 'bg-red-500'}`}
                    onDoubleClick={() => toggleStatus(cust)}
                    title={`Double click to ${cust.status === 1 || cust.status === '1' ? 'inactive' : 'active'}`}
                  >
                    {cust.status === 1 || cust.status === '1' ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2 flex items-center">
                  <button className="text-indigo-600" onClick={() => handleEdit(cust)}>
                    <Edit size={16} />
                  </button>
                  <button className="text-red-600" onClick={() => handleDelete(cust.id)}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 space-y-2 md:space-y-0">
        <div className="text-sm text-gray-600">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
        </div>
        <div className="flex justify-center space-x-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {addressOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            {addressLoading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">Addresses</h2>
              <button onClick={closeAddressModal} className="text-gray-600">×</button>
            </div>
            <ul className="space-y-2 mb-4">
              {addresses.map((a) => (
                <li key={a.id} className="border p-2 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium mr-2">{a.label}</span>
                      <span>{a.address}</span>
                      {a.isDefault && <span className="ml-1 text-xs">(Default)</span>}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="text-indigo-600" onClick={() => handleAddressEdit(a)}>
                        <Edit size={14} />
                      </button>
                      {!a.isDefault && (
                        <button className="text-yellow-500" onClick={() => handleSetDefault(a)}>
                          <Star size={14} />
                        </button>
                      )}
                      <span
                        className={`px-2 py-1 rounded text-white ${
                          a.status === 1 || a.status === '1' ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      >
                        {a.status === 1 || a.status === '1' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
              {addresses.length === 0 && <li>No addresses found</li>}
            </ul>
            <form onSubmit={handleAddressSubmit} className="grid grid-cols-1 gap-4">
              <div>
                <select
                  name="label"
                  value={addressForm.label}
                  onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="Home">Home</option>
                  <option value="Office">Office</option>
                </select>
              </div>
              <div>
                <input
                  name="address"
                  value={addressForm.address}
                  onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                  placeholder="Address"
                  className={`border px-3 py-2 rounded w-full ${addressErrors.address ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {addressErrors.address && (
                  <p className="text-red-500 text-sm mt-1">{addressErrors.address}</p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span>Status</span>
                <button
                  type="button"
                  onClick={() =>
                    setAddressForm({
                      ...addressForm,
                      status: addressForm.status === 1 ? 2 : 1,
                    })
                  }
                  className={`px-2 py-1 rounded text-white ${
                    addressForm.status === 1 ? 'bg-green-500' : 'bg-red-500'
                  }`}
                >
                  {addressForm.status === 1 ? 'Active' : 'Inactive'}
                </button>
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
                disabled={addressLoading}
              >
                {addressLoading
                  ? 'Saving...'
                  : editingAddressId
                  ? 'Update Address'
                  : 'Add Address'}
                </button>
            </form>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            {loading && (
              <div className="absolute inset-0 bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Customer' : 'Add Customer'}</h2>
              <button onClick={closeForm} className="text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
              <div>
                <input
                  name="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Name"
                  className={`border px-3 py-2 rounded w-full ${errors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone"
                  className={`border px-3 py-2 rounded w-full ${errors.phone ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              <div>
                <input
                  name="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <select
                  name="status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </select>
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
                disabled={loading}
              >
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

